import {
  Children,
  ReactElement,
  isValidElement,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { KeyboardAvoidingView, ScrollView } from 'react-native';
import { View } from 'react-native';
import Animated, {
  FadeIn,
  FadeOut,
  useAnimatedStyle,
  withTiming,
} from 'react-native-reanimated';
import { SafeAreaView } from 'react-native-safe-area-context';
import { NavigationSteps } from '@/common/components/NavigationSteps/NavigationSteps';
import {
  PageHeader,
  TPageHeaderProps,
} from '@/common/components/PageHeader/PageHeader';
import { Stack } from '@/common/components/Stack/Stack';
import { useBreakpoints } from '@/common/hooks/useBreakpoints';
import { FlowLayoutContext } from './FlowLayout.context';
import { styles } from './FlowLayout.styles';
import { dismissFlow } from './FlowLayout.utils';
import { FlowLayoutBar } from './components/FlowLayoutBar/FlowLayoutBar';
import {
  FlowLayoutDialog,
  TFlowLayoutDialogProps,
} from './components/FlowLayoutDialog/FlowLayoutDialog';
import { FlowLayoutFooter } from './components/FlowLayoutFooter/FlowLayoutFooter';
import {
  FlowLayoutContent,
  FlowLayoutStep,
  TFlowLayoutContentProps,
  TFlowLayoutStepProps,
} from './components/FlowLayoutStep/FlowLayoutStep';

export type TFlowLayoutProps = {
  headerProps?: TPageHeaderProps;
  dialogProps?: Omit<TFlowLayoutDialogProps, 'onClose'>;
  children:
    | (ReactElement<TFlowLayoutStepProps> | null | undefined | false)[]
    | ReactElement<TFlowLayoutStepProps>;
};

export function FlowLayoutRoot({
  headerProps,
  dialogProps,
  children,
}: TFlowLayoutProps) {
  const breakpoints = useBreakpoints(['lg']);
  const [isScrolled, setIsScrolled] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentContent, setCurrentContent] = useState<Omit<
    TFlowLayoutContentProps,
    'children'
  > | null>(null);
  const [shouldShowDialog, setShouldShowDialog] = useState(false);
  const [showDialog, setShowDialog] = useState(false);

  const updateFlow = useCallback(
    (contentProps: Omit<TFlowLayoutContentProps, 'children'>) => {
      setCurrentContent(contentProps);
    },
    []
  );

  const steps = useMemo(() => {
    return Children.toArray(children)
      .filter(isValidElement<TFlowLayoutStepProps>)
      .map((child) => ({
        title: child.props.title,
        description: child.props.description,
      }));
  }, [children]);

  const goNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setShouldShowDialog(true);
    } else {
      dismissFlow();
    }
  }, [steps, currentStep]);

  const goBack = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  const validChildren = useMemo(() => {
    return Children.toArray(children).filter(
      isValidElement<TFlowLayoutStepProps>
    );
  }, [children]);

  const onDismiss = useCallback(() => {
    if (shouldShowDialog) {
      setShowDialog(true);
    } else {
      dismissFlow();
    }
  }, [shouldShowDialog]);

  const canGoBack = currentStep > 0 && (currentContent?.canGoBack ?? true);
  const contextValue = useMemo(
    () => ({
      currentStep,
      updateFlow,
      goNext,
      goBack,
      dismiss: dismissFlow,
    }),
    [currentStep, updateFlow, goNext, goBack]
  );

  const animatedStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateX: withTiming(`${currentStep * -100}%`) }],
    };
  });

  return (
    <View className={styles.root}>
      <FlowLayoutContext.Provider value={contextValue}>
        {!breakpoints.lg && (
          <FlowLayoutBar
            onBack={canGoBack ? goBack : undefined}
            onDismiss={onDismiss}
          />
        )}
        <SafeAreaView className={styles.wrapper} edges={['bottom']}>
          <KeyboardAvoidingView
            behavior="padding"
            className={styles.keyboardView({ scrolled: isScrolled })}
          >
            <ScrollView
              contentContainerClassName={styles.scrollViewContainer}
              onScroll={({ nativeEvent }) => {
                setIsScrolled(nativeEvent.contentOffset.y > 1);
              }}
              scrollEventThrottle={60}
            >
              <Stack gap="lg">
                {headerProps && <PageHeader {...headerProps} />}
                {steps.length > 1 && (
                  <NavigationSteps steps={steps} currentStep={currentStep} />
                )}
              </Stack>
              <Animated.View className={styles.content} style={animatedStyle}>
                {validChildren.map((child, index) => (
                  <View className={styles.step} key={index}>
                    {currentStep === index && (
                      <Animated.View entering={FadeIn} exiting={FadeOut}>
                        {child}
                      </Animated.View>
                    )}
                  </View>
                ))}
              </Animated.View>
            </ScrollView>
            {currentContent && (
              <FlowLayoutFooter
                {...currentContent}
                canGoBack={canGoBack}
                onDismiss={onDismiss}
              />
            )}
          </KeyboardAvoidingView>
        </SafeAreaView>
      </FlowLayoutContext.Provider>
      {showDialog && (
        <FlowLayoutDialog
          {...dialogProps}
          onClose={() => setShowDialog(false)}
        />
      )}
    </View>
  );
}

export const FlowLayout = {
  Root: FlowLayoutRoot,
  Step: FlowLayoutStep,
  Content: FlowLayoutContent,
};
